import { inject, Injectable } from '@angular/core';
import { groupBy } from 'es-toolkit/compat';
import { Observable, of } from 'rxjs';

import { ReviewResponseDto } from '@malou-io/package-dto';
import { CsvAsStringArrays } from '@malou-io/package-utils';

import { LightNfc } from '#modules/nfcs/models/nfc';
import { ScanForRestaurantInsights } from '#modules/scan/models/scan';
import {
    AbstractCsvService,
    DataWithNilExcluded,
} from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { StatisticsStore } from '#modules/statistics/stores/store/statistics.store';
import { STARS_RATING } from '#shared/constants/index';
import { NfcToCsvMapper } from '#shared/helpers/index';

interface Data {
    nfcs: LightNfc[] | undefined;
    scans: ScanForRestaurantInsights[] | undefined;
    privateReviews: ReviewResponseDto[] | undefined;
}

@Injectable()
export class BoostersReviewsCountCsvInsightV2Service extends AbstractCsvService<Data> {
    private readonly _statisticsStore = inject(StatisticsStore);
    private readonly _nfcToCsvMapper = inject(NfcToCsvMapper);

    protected _getData$(): Observable<Data> {
        return of({
            nfcs: this._statisticsStore.boosterStatsDataV2()?.nfcs,
            scans: this._statisticsStore.boosterStatsDataV2()?.scans,
            privateReviews: this._statisticsStore.privateReviewsData(),
        });
    }

    protected override _isDataValid(data: Data): boolean {
        return !!data;
    }

    protected override _getCsvHeaderRow(): string[] {
        return ['Booster', 'Rating', 'Private Review Count', 'Collected Reviews Estimate'];
    }

    protected override _getCsvDataRows(data: DataWithNilExcluded<Data>): CsvAsStringArrays {
        const { nfcs, scans, privateReviews } = data;
        const scansById = groupBy(scans, (el) => el.id.toString());
        const groupedData = nfcs.reduce((acc: string[][], nfc) => {
            const nfcScans = scans.filter((scan) => nfc.id === scan.nfcId);
            const nfcPrivateReviewsScan: ReviewResponseDto[] = [];
            privateReviews.forEach((privateReview) => {
                if (privateReview.scanId) {
                    const scan = scansById[privateReview.scanId.toString()];
                    if (scan && scan[0].nfcId === nfc.id) {
                        nfcPrivateReviewsScan.push(privateReview);
                    }
                }
            });
            const nfcName = this._nfcToCsvMapper.mapNfcName(nfc);
            STARS_RATING.forEach((rating) => {
                const scansFiltered = nfcScans?.filter(
                    (scan) => scan.matchedReview?.id && (scan.starClicked === rating || scan.matchedReview?.rating === rating)
                );
                const privateReviewsFiltered = nfcPrivateReviewsScan.filter((privateReview) => privateReview.rating === rating);
                acc.push([nfcName, rating.toString(), privateReviewsFiltered.length.toString(), scansFiltered?.length.toString() ?? '0']);
            });
            return acc;
        }, []);
        return groupedData ?? [];
    }
}
